/**
 * The Practice Manager's morning briefing as a TanStack query: restored
 * from the browser cache, revalidated in the background and (debounced)
 * when the practice summary changes. Never blocks Home from opening.
 */
import { useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import { queryClient } from "@/lib/query/queryClient";

import { DashboardAPI } from "../api/dashboardApi";
import type { BriefingResponse } from "../types";

export const briefingQueryKey = (practice: string | null) =>
  ["dashboard-briefing", practice] as const;

export type BriefingState = {
  data: BriefingResponse | null;
  isLoading: boolean;
  isRefreshing: boolean;
  error: string | null;
  /** Ask the model to write the briefing again. */
  regenerate: () => Promise<void>;
};

export const useBriefing = (
  practice: string | null,
  { enabled = true }: { enabled?: boolean } = {}
): BriefingState => {
  const [forcing, setForcing] = useState(false);
  const on = enabled && Boolean(practice);
  const query = useQuery(
    {
      queryKey: briefingQueryKey(practice),
      queryFn: () => DashboardAPI.getBriefing(),
      enabled: on,
      meta: { persist: true }
    },
    queryClient
  );

  const regenerate = useCallback(async () => {
    if (!practice) return;
    setForcing(true);
    try {
      const next = await DashboardAPI.getBriefing({ refresh: true });
      queryClient.setQueryData(briefingQueryKey(practice), next);
    } finally {
      setForcing(false);
    }
  }, [practice]);

  const data = query.data ?? null;
  return {
    data,
    isLoading: on && !data && query.isPending,
    isRefreshing: forcing || (Boolean(data) && query.isFetching),
    error: query.error
      ? query.error instanceof Error
        ? query.error.message
        : "Couldn't load the briefing."
      : null,
    regenerate
  };
};
